import { apiRequest } from '../api/client';
import type { BillingConceptType } from '../invoices/billing-concepts-api';

export const BILLING_CYCLES = ['WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quincenal',
  MONTHLY: 'Mensual',
};

export const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'EXPIRED', 'TERMINATED'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  EXPIRED: 'Vencido',
  TERMINATED: 'Dado de baja',
};

/** ISO weekday, 1 = lunes … 7 = domingo, same numbering `createContractSchema` (`apps/api`) accepts. */
export const SERVICE_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export const SERVICE_DAY_LABELS: Record<(typeof SERVICE_DAYS)[number], string> = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo',
};

/** Matches `apps/api` `StoredContract` (`contract.repository.ts`) response shape. */
export interface ContractListItem {
  id: string;
  customerId: string;
  operatingUnitId: string;
  code: string;
  version: number;
  parentContractId: string | null;
  startDate: string;
  endDate: string | null;
  status: ContractStatus;
  billingCycle: BillingCycle;
  paymentTermDays: number;
  cutoffTime: string | null;
  cutoffDaysBefore: number | null;
  minDailyQuantity: number | null;
  maxDailyQuantity: number | null;
  allowsCutoffOverride: boolean;
  serviceDays: number[];
  includesHolidays: boolean;
  documentUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Matches `apps/api` `ContractDetail` (`get-contract-detail.use-case.ts`). */
export interface ContractDetail extends ContractListItem {
  supersededByVersion: number | null;
  effectiveCutoffTime: string;
  effectiveCutoffDaysBefore: number;
}

/**
 * `apps/api` `GET /contracts` (`ListContractsUseCase`) returns every
 * contract within the caller's data scope, unfiltered and unpaginated
 * (`contracts.controller.ts`), narrowed to one customer when `customerId`
 * is given. Status and code search still happen client-side over the
 * resulting list, same criterion `design.md` §10.3 already accepted for
 * the volume expected in this module.
 */
export function listContracts(customerId?: string): Promise<ContractListItem[]> {
  const query = customerId === undefined ? '' : `?customerId=${customerId}`;

  return apiRequest<ContractListItem[]>(`/contracts${query}`);
}

/** Matches `apps/api` `GET /contracts/:id` (`get-contract-detail.use-case.ts`). */
export function getContract(id: string): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${id}`);
}

export interface ContractFormInput {
  code: string;
  customerId: string;
  operatingUnitId: string;
  startDate: string;
  endDate?: string | null;
  billingCycle: BillingCycle;
  paymentTermDays: number;
  cutoffTime?: string | null;
  cutoffDaysBefore?: number | null;
  minDailyQuantity?: number | null;
  maxDailyQuantity?: number | null;
  allowsCutoffOverride?: boolean;
  serviceDays?: number[];
  includesHolidays?: boolean;
  notes?: string | null;
}

/** Matches `apps/api` `POST /contracts` (`createContractSchema`). */
export function createContract(input: ContractFormInput): Promise<ContractDetail> {
  return apiRequest<ContractDetail>('/contracts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ContractUpdateInput {
  /** Only honoured on a `DRAFT` contract: the point of sale is immutable once it leaves `DRAFT`. */
  operatingUnitId?: string;
  billingCycle?: BillingCycle;
  paymentTermDays?: number;
  cutoffTime?: string | null;
  cutoffDaysBefore?: number | null;
  minDailyQuantity?: number | null;
  maxDailyQuantity?: number | null;
  allowsCutoffOverride?: boolean;
  serviceDays?: number[];
  includesHolidays?: boolean;
  notes?: string | null;
  endDate?: string | null;
  /** Only meaningful when the current version is `ACTIVE`; ignored on `DRAFT`. */
  effectiveDate?: string;
}

/**
 * Matches `apps/api` `PATCH /contracts/:id` (`updateContractSchema`);
 * `input` is only the changed fields, same convention as `updateCustomer`.
 * On an `ACTIVE` contract this generates a new version server-side
 * (`contracts/spec.md` → "Edición de contrato activo genera nueva versión").
 */
export function updateContract(id: string, input: ContractUpdateInput): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `POST /contracts/:id/activate`. */
export function activateContract(id: string): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${id}/activate`, { method: 'POST' });
}

/** Matches `apps/api` `POST /contracts/:id/pause` (`reason` optional). */
export function pauseContract(id: string, reason: string | null): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${id}/pause`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Matches `apps/api` `POST /contracts/:id/terminate` (`reason` mandatory). */
export function terminateContract(id: string, reason: string): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${id}/terminate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Matches `apps/api` `StoredContractPrice` (`contract-price.repository.ts`). */
export interface ContractPrice {
  id: string;
  contractId: string;
  menuId: string;
  unitPriceCents: number;
  vatRate: number;
  validFrom: string;
  validTo: string | null;
  isAvailable: boolean;
}

export interface AddContractPriceInput {
  menuId: string;
  unitPriceCents: number;
  vatRate: number;
  validFrom: string;
  validTo?: string | null;
  isAvailable?: boolean;
}

/** Matches `apps/api` `GET /contracts/:id/prices` (`list-contract-prices.use-case.ts`). */
export function listContractPrices(contractId: string): Promise<ContractPrice[]> {
  return apiRequest<ContractPrice[]>(`/contracts/${contractId}/prices`);
}

/** Matches `apps/api` `POST /contracts/:id/prices` (`addContractPriceSchema`). */
export function addContractPrice(
  contractId: string,
  input: AddContractPriceInput,
): Promise<ContractPrice> {
  return apiRequest<ContractPrice>(`/contracts/${contractId}/prices`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface UpdateContractPriceInput {
  unitPriceCents?: number;
  vatRate?: number;
  validFrom?: string;
  validTo?: string | null;
  isAvailable?: boolean;
}

/** Matches `apps/api` `PATCH /contracts/:id/prices/:priceId` (`updateContractPriceSchema`); every field optional. */
export function updateContractPrice(
  contractId: string,
  priceId: string,
  input: UpdateContractPriceInput,
): Promise<ContractPrice> {
  return apiRequest<ContractPrice>(`/contracts/${contractId}/prices/${priceId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `StoredContractServiceTypePrice` (`contract-service-type-price.repository.ts`). */
export interface ContractServiceTypePrice {
  id: string;
  contractId: string;
  serviceTypeId: string;
  unitPriceCents: number;
  vatRate: number;
  validFrom: string;
  validTo: string | null;
  isAvailable: boolean;
  billingConcept: ContractPriceBillingConcept;
}

/** Concept summary embedded in each service-type price row (`contract-service-type-price.repository.ts`). */
export interface ContractPriceBillingConcept {
  id: string;
  name: string;
  arcaType: BillingConceptType;
  isActive: boolean;
}

export interface AddContractServiceTypePriceInput {
  serviceTypeId: string;
  billingConceptId: string;
  unitPriceCents: number;
  vatRate: number;
  validFrom: string;
  validTo?: string | null;
  isAvailable?: boolean;
}

/** Matches `apps/api` `GET /contracts/:id/service-type-prices` (`list-contract-service-type-prices.use-case.ts`). */
export function listContractServiceTypePrices(
  contractId: string,
): Promise<ContractServiceTypePrice[]> {
  return apiRequest<ContractServiceTypePrice[]>(`/contracts/${contractId}/service-type-prices`);
}

/** Matches `apps/api` `POST /contracts/:id/service-type-prices` (`addContractServiceTypePriceSchema`). */
export function addContractServiceTypePrice(
  contractId: string,
  input: AddContractServiceTypePriceInput,
): Promise<ContractServiceTypePrice> {
  return apiRequest<ContractServiceTypePrice>(`/contracts/${contractId}/service-type-prices`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface UpdateContractServiceTypePriceInput {
  billingConceptId?: string;
  unitPriceCents?: number;
  vatRate?: number;
  validFrom?: string;
  validTo?: string | null;
  isAvailable?: boolean;
}

/** Matches `apps/api` `PATCH /contracts/:id/service-type-prices/:priceId` (`updateContractServiceTypePriceSchema`); every field optional. */
export function updateContractServiceTypePrice(
  contractId: string,
  priceId: string,
  input: UpdateContractServiceTypePriceInput,
): Promise<ContractServiceTypePrice> {
  return apiRequest<ContractServiceTypePrice>(
    `/contracts/${contractId}/service-type-prices/${priceId}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export interface BulkPriceChange {
  kind: 'SERVICE_TYPE' | 'MENU';
  targetId: string;
  targetName: string;
  oldPriceCents: number;
  newPriceCents: number;
  reason: string;
}

export interface BulkUpdateContractPricesResult {
  changes: BulkPriceChange[];
}

/**
 * Matches `apps/api` `PATCH /contracts/:id/prices/bulk-update`
 * (`bulkUpdateContractPricesSchema`). `preview: true` never persists;
 * `contract-prices/spec.md` → "Confirmación requerida antes de aplicar
 * cambio masivo".
 */
export function bulkUpdateContractPrices(
  contractId: string,
  percentage: number,
  preview: boolean,
): Promise<BulkUpdateContractPricesResult> {
  return apiRequest<BulkUpdateContractPricesResult>(`/contracts/${contractId}/prices/bulk-update`, {
    method: 'PATCH',
    body: JSON.stringify({ percentage, preview }),
  });
}

export interface RequestDocumentUploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresAt: string;
}

/** Matches `apps/api` `POST /contracts/:id/document/upload-url`. */
export function requestDocumentUploadUrl(
  contractId: string,
  contentType: string,
  sizeBytes: number,
): Promise<RequestDocumentUploadUrlResult> {
  return apiRequest<RequestDocumentUploadUrlResult>(
    `/contracts/${contractId}/document/upload-url`,
    {
      method: 'POST',
      body: JSON.stringify({ contentType, sizeBytes }),
    },
  );
}

/** Matches `apps/api` `POST /contracts/:id/document/confirm`. */
export function confirmDocumentUpload(contractId: string, key: string): Promise<ContractDetail> {
  return apiRequest<ContractDetail>(`/contracts/${contractId}/document/confirm`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

export interface RequestDocumentDownloadUrlResult {
  downloadUrl: string;
  expiresAt: string;
}

/** Matches `apps/api` `GET /contracts/:id/document/download-url`. */
export function requestDocumentDownloadUrl(
  contractId: string,
): Promise<RequestDocumentDownloadUrlResult> {
  return apiRequest<RequestDocumentDownloadUrlResult>(
    `/contracts/${contractId}/document/download-url`,
  );
}
